import type { ConstantsBillingUsageItem } from "@gosaas/types";
import {
	IconBuilding,
	IconFolder,
	IconLink,
	IconTag,
	IconTrendingUp,
	IconUsers,
} from "@tabler/icons-react";
import type { BillingRangeType } from "@/components/providers/billing-usage-range";

export type BillingUsageItem = ConstantsBillingUsageItem;

export type { BillingRangeType };

/** Features tracked internally but not shown on the usage dashboard. */
const BILLING_USAGE_HIDDEN_FEATURES = new Set([
	"audit_retention_days",
	"api_access",
]);

export function filterBillingUsageForDisplay(
	items: BillingUsageItem[],
): BillingUsageItem[] {
	return items.filter(
		(item) => !BILLING_USAGE_HIDDEN_FEATURES.has(item.feature_key),
	);
}

export function getFeatureIcon(featureKey: string) {
	switch (featureKey) {
		case "seats":
			return IconUsers;
		case "child_teams":
			return IconBuilding;
		case "folders":
			return IconFolder;
		case "sources":
			return IconLink;
		case "tags":
			return IconTag;
		default:
			return IconTrendingUp;
	}
}

export function formatFeatureName(featureKey: string): string {
	const formatted = featureKey.replace(/_/g, " ");
	return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

export function keyMetricsAttentionLabel(featureCount: number): string {
	return `Top ${featureCount} feature${featureCount !== 1 ? "s" : ""} requiring attention`;
}

export function applyUsageRangeSelection(
	value: string | undefined,
): BillingRangeType | undefined {
	return value ? (value as BillingRangeType) : undefined;
}

export function handleUsageRangeValueChange(
	value: string,
	setRange: (range: BillingRangeType) => void,
): void {
	const next = applyUsageRangeSelection(value);
	if (next) setRange(next);
}

export function applyUsageChartFeatureSelection(
	value: string | undefined,
): string {
	return value ?? "all";
}

export function utilizationBadgeVariant(
	status: ReturnType<typeof getUsageStatus>["status"],
): "destructive" | "secondary" | "default" {
	if (status === "critical") {
		return "destructive";
	}
	if (status === "warning") {
		return "secondary";
	}
	return "default";
}

export function getUsageStatus(
	usage: number,
	limit: number | null | undefined,
): { status: "normal" | "warning" | "critical"; color: string } {
	if (limit == null || limit === 0)
		return { status: "normal", color: "hsl(var(--muted-foreground))" };

	const percentage = (usage / limit) * 100;
	if (percentage >= 90)
		return { status: "critical", color: "hsl(var(--destructive))" };
	if (percentage >= 75)
		return { status: "warning", color: "hsl(var(--warning))" };
	return { status: "normal", color: "hsl(var(--primary))" };
}

export function usageLimit(limit: number | null | undefined): number | null {
	return limit != null && limit > 0 ? limit : null;
}

export function shouldFetchBillingUsage(
	teamID: string | null | undefined,
	canReadBilling: boolean,
	range: BillingRangeType,
	start: string | null,
	end: string | null,
): boolean {
	if (!teamID || !canReadBilling) {
		return false;
	}
	if (range === "custom" && (!start || !end)) {
		return false;
	}
	return true;
}

export function formatUsagePeriodBadgeLabel(range: BillingRangeType): string {
	if (range === "current") {
		return "Current Period";
	}
	if (range === "previous") {
		return "Previous Period";
	}
	if (range === "custom") {
		return "Custom Range";
	}
	return "Current Period";
}

const usageDateFormat: Intl.DateTimeFormatOptions = {
	month: "short",
	day: "numeric",
	year: "numeric",
};

export function formatUsageHeaderDateRange(
	range: BillingRangeType,
	start: string | null,
	end: string | null,
	usageFirst?: { period_start: string; period_end: string } | null,
): string {
	if (range === "custom") {
		if (start && end) {
			return `${new Date(start).toLocaleDateString("en-US", usageDateFormat)} - ${new Date(end).toLocaleDateString("en-US", usageDateFormat)}`;
		}
		return "Select dates";
	}
	if (usageFirst) {
		return `${new Date(usageFirst.period_start).toLocaleDateString("en-US", usageDateFormat)} - ${new Date(usageFirst.period_end).toLocaleDateString("en-US", usageDateFormat)}`;
	}
	return "No data available";
}

export function getBillingRangeLabel(r: BillingRangeType): string {
	switch (r) {
		case "current":
			return "Current period";
		case "previous":
			return "Previous period";
		case "custom":
			return "Custom range";
		default:
			return "Current period";
	}
}

export function sortUsageChartData<T extends { period_start: string }>(
	chartData: T[],
): T[] {
	return [...chartData].sort(
		(a, b) =>
			new Date(a.period_start).getTime() - new Date(b.period_start).getTime(),
	);
}

export function getKeyFeatures(data: BillingUsageItem[]): BillingUsageItem[] {
	const sorted = [...data].sort((a, b) => {
		const aStatus = getUsageStatus(a.usage, a.limit).status;
		const bStatus = getUsageStatus(b.usage, b.limit).status;

		if (aStatus === "critical" && bStatus !== "critical") return -1;
		if (bStatus === "critical" && aStatus !== "critical") return 1;

		if (aStatus === "warning" && bStatus !== "warning") return -1;
		if (bStatus === "warning" && aStatus !== "warning") return 1;

		const aPercentage = a.limit ? (a.usage / a.limit) * 100 : 0;
		const bPercentage = b.limit ? (b.usage / b.limit) * 100 : 0;
		return bPercentage - aPercentage;
	});

	return sorted.slice(0, 3);
}
